import React, { useState } from 'react';
import { Activity, Droplet, Moon, Heart, Wind, Plus, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';

const HealthLogger = ({ onUpdate }) => {
  const [formData, setFormData] = useState({
    steps: '',
    water: '',
    sleep: '',
    heart_rate: '',
    breathing_exercises: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const healthMetrics = [
    { id: 'steps', label: 'Steps', icon: Activity, unit: 'steps', color: 'blue', placeholder: '10000' },
    { id: 'water', label: 'Water Intake', icon: Droplet, unit: 'liters', color: 'cyan', placeholder: '2.5' },
    { id: 'sleep', label: 'Sleep Duration', icon: Moon, unit: 'hours', color: 'purple', placeholder: '8' },
    { id: 'heart_rate', label: 'Heart Rate', icon: Heart, unit: 'bpm', color: 'red', placeholder: '72' },
    { id: 'breathing_exercises', label: 'Breathing Minutes', icon: Wind, unit: 'minutes', color: 'green', placeholder: '10' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if at least one field is filled
    const hasData = Object.values(formData).some(val => val !== '');
    if (!hasData) {
      setError('Please enter at least one health metric');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('shansozen_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shansozen/log-health`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          steps: formData.steps ? parseInt(formData.steps) : null,
          water: formData.water ? parseFloat(formData.water) : null,
          sleep: formData.sleep ? parseFloat(formData.sleep) : null,
          heart_rate: formData.heart_rate ? parseInt(formData.heart_rate) : null,
          breathing_exercises: formData.breathing_exercises ? parseInt(formData.breathing_exercises) : null
        })
      });

      if (response.ok) {
        setSuccess('Health data logged successfully!');
        setFormData({
          steps: '',
          water: '',
          sleep: '',
          heart_rate: '',
          breathing_exercises: ''
        });
        if (onUpdate) onUpdate();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to log data. Please try again.');
      }
    } catch (error) {
      console.error('Logging error:', error);
      setError('Network error. Please check your connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Activity className="w-8 h-8 text-blue-400" />
        <div>
          <h2 className="text-3xl font-bold text-white">Health Data Logger</h2>
          <p className="text-gray-400">Track your daily health metrics</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {healthMetrics.map((metric) => {
            const Icon = metric.icon;
            return (
              <div key={metric.id} className={`bg-${metric.color}-500/10 border border-${metric.color}-500/30 rounded-xl p-6`}>
                <div className="flex items-center gap-3 mb-4">
                  <Icon className={`w-6 h-6 text-${metric.color}-400`} />
                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-white mb-1">
                      {metric.label}
                    </label>
                    <p className="text-xs text-gray-400">{metric.unit}</p>
                  </div>
                </div>
                <input
                  type="number"
                  step="any"
                  value={formData[metric.id]}
                  onChange={(e) => handleChange(metric.id, e.target.value)}
                  placeholder={metric.placeholder}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>
            );
          })}
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-400">{success}</p>
          </div>
        )}

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:opacity-50 py-4 text-lg"
        >
          {isSubmitting ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Logging...
            </>
          ) : (
            <>
              <Plus className="w-5 h-5 mr-2" />
              Log Health Data
            </>
          )}
        </Button>
      </form>
    </div>
  );
};

export default HealthLogger;
